import type { ProgressListener, ProgressRepo } from '@dcs/platform/ports';

/**
 * The `ProgressRepo` screens hold for the whole session. It forwards to one inner repo (local for
 * a guest, the syncing one once signed in) and swaps it without the screens noticing: listeners
 * stay subscribed, the next call goes to the new repo.
 */
export interface SwitchableProgressRepo extends ProgressRepo {
  /** Routes every call to `repo` from now on. */
  use(repo: ProgressRepo): void;
}

export function createSwitchableProgressRepo(initial: ProgressRepo): SwitchableProgressRepo {
  let inner = initial;
  const listeners = new Set<ProgressListener>();
  const forward: ProgressListener = (licenseClass, progress) => {
    for (const listener of [...listeners]) listener(licenseClass, progress);
  };
  let stopForwarding = inner.subscribe(forward);

  return {
    load: (licenseClass) => inner.load(licenseClass),
    recordAnswer: (licenseClass, questionId, choice, correct, now) =>
      inner.recordAnswer(licenseClass, questionId, choice, correct, now),
    setBookmark: (licenseClass, questionId, on, now) =>
      inner.setBookmark(licenseClass, questionId, on, now),
    clearWrong: (licenseClass, now) => inner.clearWrong(licenseClass, now),
    reset: (licenseClass) => inner.reset(licenseClass),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    use(repo) {
      if (repo === inner) return;
      stopForwarding();
      inner = repo;
      stopForwarding = inner.subscribe(forward);
    },
  };
}
